import { addDays, busyIntervals, commit, createEvent, Temporal, type AppState, type Change, type OpResult } from '@autoday/core';

export interface CapacityOptions {
  title: string;
  minutes: number;
  deadline: string;
  windowStart: string;
  windowEnd: string;
  weekdays: number[];
  sessionMinutes: number;
}
export interface CapacitySession { start: string; end: string; minutes: number }
export interface CapacityProposal {
  options: CapacityOptions;
  timeZone: string;
  sessions: CapacitySession[];
  plannedMinutes: number;
  missingMinutes: number;
  uncertainCount: number;
  hiddenCount: number;
}
export class CapacityError extends Error {
  constructor(readonly code: 'invalid' | 'range' | 'stale' | 'partial') { super(code); }
}
const minutesOf = (s: string) => {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(s)) throw new CapacityError('invalid');
  const [h, m] = s.split(':').map(Number);
  return h! * 60 + m!;
};
const localAt = (date: string, minutes: number) => `${date}T${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}:00`;
// Reject both ambiguous and nonexistent local times. Never stretch a work session across DST.
const instantAt = (local: string, zone: string) => Temporal.PlainDateTime.from(local).toZonedDateTime(zone, { disambiguation: 'reject' }).epochMilliseconds;

/** Read-only, bounded simulation on the current device's calendar snapshot. Existing events never move. */
export function proposeCapacity(state: AppState, input: CapacityOptions, now: Temporal.Instant): CapacityProposal {
  const options = { ...input, weekdays: [...input.weekdays] };
  const tz = state.settings.timeZone;
  const today = now.toZonedDateTimeISO(tz).toPlainDate();
  const from = minutesOf(options.windowStart), to = minutesOf(options.windowEnd);
  if (!options.title.trim() || options.title.length > 300 || !Number.isInteger(options.minutes) || options.minutes < 15 || options.minutes > 2400 || options.minutes % 15 ||
      !Number.isInteger(options.sessionMinutes) || options.sessionMinutes < 15 || options.sessionMinutes > 120 || options.sessionMinutes % 15 ||
      from >= to || from % 15 || to % 15 || !options.weekdays.length || options.weekdays.some(d => !Number.isInteger(d) || d < 1 || d > 7)) throw new CapacityError('invalid');
  let deadlineMs: number, deadlineDate: string;
  try {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:00)?$/.test(options.deadline)) throw new Error();
    const deadline = Temporal.PlainDateTime.from(options.deadline);
    deadlineDate = deadline.toPlainDate().toString();
    deadlineMs = instantAt(options.deadline, tz);
    const days = today.until(deadline.toPlainDate()).days;
    if (days < 0 || days > 20 || deadlineMs <= now.epochMilliseconds) throw new CapacityError('range');
  } catch (e) { if (e instanceof CapacityError) throw e; throw new CapacityError('invalid'); }
  const { busy, uncertain, hiddenCalendarsCounted } = busyIntervals(state, today.toString(), addDays(deadlineDate, 1));
  const sessions: CapacitySession[] = [];
  let left = options.minutes;
  for (let date = today.toString(); date <= deadlineDate && left > 0; date = addDays(date, 1)) {
    if (!options.weekdays.includes(Temporal.PlainDate.from(date).dayOfWeek)) continue;
    let m = from;
    while (m + 15 <= to && left > 0) {
      let count = 0;
      const limit = Math.min(options.sessionMinutes, left, to - m);
      for (let length = 15; length <= limit; length += 15) {
        try {
          const start = instantAt(localAt(date, m), tz), end = instantAt(localAt(date, m + length), tz);
          if (start < now.epochMilliseconds || end > deadlineMs || end - start !== length * 60000 || busy.some(b => b.start < end && b.end > start)) break;
          count = length;
        } catch { break; }
      }
      if (count) {
        sessions.push({ start: localAt(date, m), end: localAt(date, m + count), minutes: count });
        left -= count;
        m += count + 15; // A visible, fixed 15-minute pause between proposed sessions.
      } else m += 15;
    }
  }
  return { options, timeZone: tz, sessions, plannedMinutes: options.minutes - left, missingMinutes: left, uncertainCount: uncertain.length, hiddenCount: hiddenCalendarsCounted };
}

/** Recheck at approval time, then create the entire plan in one existing store transaction, with one undo. */
export function commitCapacity(state: AppState, opId: string, proposal: CapacityProposal, calendarId: string, now: Temporal.Instant): OpResult {
  if (state.appliedOps[opId]) return { state, change: {}, undo: null, duplicate: true };
  if (proposal.missingMinutes || !proposal.sessions.length) throw new CapacityError('partial');
  const current = proposeCapacity(state, proposal.options, now);
  if (current.timeZone !== proposal.timeZone || current.missingMinutes || JSON.stringify(current.sessions) !== JSON.stringify(proposal.sessions)) throw new CapacityError('stale');
  const events: NonNullable<Change['events']> = {};
  for (const [i, slot] of current.sessions.entries()) {
    const result = createEvent(state, `${opId}:${i}`, {
      calendarId, title: proposal.options.title.trim(), categoryId: state.calendars[calendarId]?.kind === 'work' ? 'arbete' : 'privat', icon: 'briefcase', alarms: [],
      timing: { kind: 'timed', start: slot.start, end: slot.end, timeZone: current.timeZone, durationPolicy: 'elapsed' },
    }, now);
    Object.assign(events, result.change.events);
  }
  return commit(state, opId, proposal.options.title.trim(), { events }, now);
}
